import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type {
  PatientContextEntry,
  PatientContentResponse,
} from '@viome/viome-base/src/usecase/PatientContext/PatientContext.model';
import type { GetInitData } from '@viome/viome-base/src/usecase/Bootup/BootupModel';
import {
  setActiveKitId as syncActiveKitId,
  setActivePreRnrKitId as syncActivePreRnrKitId,
} from '@viome/viome-base/src/api/activeKit';
import { getPersistedCancerDetectClicked } from '@hooks/useCancerDetectUser';

/**
 * Patient Context store (DE-336).
 *
 * Holds both context payloads (consumer = getInit v2, patient = content API)
 * and the active selection. The active context decides which payload feeds
 * `useBootup`. Session-only (sessionStorage) — context is re-established each
 * login. `selectConsumer`/`selectOrg` are invoked both from the one-time
 * post-login picker/auto-resolver AND live, in-app, from
 * `DashboardLayoutContextSwitcher` (sidebar) — so a multi-context user can
 * flip between Consumer and a partner org without logging out.
 */
export interface PatientOrgState {
  id: number;
  name: string;
}

export interface IContextStore {
  /** Active partner org; null means consumer context. */
  patientOrg: PatientOrgState | null;
  /** Kit currently in patient context (used for per-kit API scoping). */
  activeKitId: string | null;
  /** Consumer bootup payload (getInit v2). */
  consumerInit: GetInitData | null;
  /** Patient bootup payload (content API). */
  patientContent: PatientContentResponse | null;
  /** Patient orgs available to this user (= patientContent.entries). */
  orgs: PatientContextEntry[];
  /** Whether the user also has a (non-patient) consumer experience. */
  consumerExists: boolean;
  /** Whether the user has explicitly picked a context (consumer or an org). */
  hasSelectedContext: boolean;

  /** Populate available experiences (does not change the current selection). */
  hydrate: (input: {
    consumerInit: GetInitData | null;
    patientContent: PatientContentResponse | null;
    consumerExists: boolean;
  }) => void;
  /**
   * Choose the consumer experience. Pass `preferCD` when entering via the
   * CancerDetect switcher entry so the active kit resolves to the CD kit (not
   * the wellness kit) for a consumer holding both.
   */
  selectConsumer: (preferCD?: boolean) => void;
  /** Choose a partner org by id (sets patientOrg + activeKitId). */
  selectOrg: (orgId: number) => void;
  setPatientOrg: (org: PatientOrgState | null) => void;
  setActiveKitId: (kitId: string | null) => void;
  reset: () => void;
}

const initialState = {
  patientOrg: null,
  activeKitId: null,
  consumerInit: null,
  patientContent: null,
  orgs: [] as PatientContextEntry[],
  consumerExists: false,
  hasSelectedContext: false,
};

/**
 * The active RESULTS kit id of an entry — `kitId` when present, else
 * `PreRnrKitId`. Wellness kit first, else CD. Used by post-RnR / results reads
 * (education, forethought).
 */
const entryKitId = (entry: PatientContextEntry): string | null =>
  entry.kitDetails?.kitId ??
  entry.kitDetails?.PreRnrKitId ??
  entry.CDkitDetails?.kitId ??
  entry.CDkitDetails?.PreRnrKitId ??
  null;

/**
 * The active CURRENT-JOURNEY kit id of an entry — `PreRnrKitId` FIRST, else
 * `kitId`. Used by the pre-RnR home journey + onboarding questionnaire. For a
 * reissued kit (both ids present) this is the new pre-RnR kit, not the old
 * already-RnR'd one.
 */
const entryPreRnrKitId = (entry: PatientContextEntry): string | null =>
  entry.kitDetails?.PreRnrKitId ??
  entry.kitDetails?.kitId ??
  entry.CDkitDetails?.PreRnrKitId ??
  entry.CDkitDetails?.kitId ??
  null;

/** Consumer RESULTS kit id from getInit v2 (kitId first, else PreRnrKitId). */
const consumerKitId = (init: GetInitData | null): string | null =>
  init?.kitDetails?.kitId ??
  init?.kitDetails?.PreRnrKitId ??
  init?.CDkitDetails?.kitId ??
  init?.CDkitDetails?.PreRnrKitId ??
  null;

/** Consumer CancerDetect RESULTS kit id (CD kitId first, else CD PreRnrKitId). */
const consumerCDKitId = (init: GetInitData | null): string | null =>
  init?.CDkitDetails?.kitId ??
  init?.CDkitDetails?.PreRnrKitId ??
  null;

/** Consumer CancerDetect CURRENT-JOURNEY kit id (PreRnrKitId first, else kitId). */
const consumerCDPreRnrKitId = (init: GetInitData | null): string | null =>
  init?.CDkitDetails?.PreRnrKitId ??
  init?.CDkitDetails?.kitId ??
  null;

/** Consumer CURRENT-JOURNEY kit id (PreRnrKitId first, else kitId). */
const consumerPreRnrKitId = (init: GetInitData | null): string | null =>
  init?.kitDetails?.PreRnrKitId ??
  init?.kitDetails?.kitId ??
  init?.CDkitDetails?.PreRnrKitId ??
  init?.CDkitDetails?.kitId ??
  null;

/**
 * Recompute the active kit ids from persisted state using the CURRENT logic —
 * so a reload (where the post-login resolver doesn't re-run) still yields the
 * right kit ids, even if the persisted `activeKitId` scalar is stale.
 */
const resolveActiveKitId = (
  state: IContextStore,
  preferCD: boolean,
): string | null => {
  if (state.patientOrg) {
    const entry = state.orgs?.find((o) => o.org.id === state.patientOrg?.id);
    return entry ? entryKitId(entry) : null;
  }
  const cdKitId = consumerCDKitId(state.consumerInit);
  return preferCD && cdKitId ? cdKitId : consumerKitId(state.consumerInit);
};

const resolveActivePreRnrKitId = (
  state: IContextStore,
  preferCD: boolean,
): string | null => {
  if (state.patientOrg) {
    const entry = state.orgs?.find((o) => o.org.id === state.patientOrg?.id);
    return entry ? entryPreRnrKitId(entry) : null;
  }
  const cdKitId = consumerCDKitId(state.consumerInit);
  return preferCD && cdKitId
    ? consumerCDPreRnrKitId(state.consumerInit)
    : consumerPreRnrKitId(state.consumerInit);
};

export const useContextStore = create<IContextStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      hydrate: ({ consumerInit, patientContent, consumerExists }) =>
        set({
          consumerInit,
          patientContent,
          orgs: patientContent?.entries ?? [],
          consumerExists,
        }),
      selectConsumer: (preferCD = false) => {
        // Consumer context: active kit = the getInit v2 consumer kit (wellness
        // first, else CD), so kit-scoped APIs (home v2) can pass kit_id. When
        // entered via the CancerDetect switcher entry, scope to the CD kit so
        // CD-filtered reads resolve against it rather than the wellness kit.
        const init = get().consumerInit;
        const cdKitId = consumerCDKitId(init);
        const kitId = preferCD && cdKitId ? cdKitId : consumerKitId(init);
        const preRnrKitId =
          preferCD && cdKitId
            ? consumerCDPreRnrKitId(init)
            : consumerPreRnrKitId(init);
        set({ patientOrg: null, activeKitId: kitId, hasSelectedContext: true });
        syncActiveKitId(kitId);
        syncActivePreRnrKitId(preRnrKitId);
      },
      selectOrg: (orgId) => {
        const entry = get().orgs.find((o) => o.org.id === orgId);
        if (!entry) return;
        const kitId = entryKitId(entry);
        set({
          patientOrg: { id: entry.org.id, name: entry.org.name },
          activeKitId: kitId,
          hasSelectedContext: true,
        });
        syncActiveKitId(kitId);
        syncActivePreRnrKitId(entryPreRnrKitId(entry));
      },
      setPatientOrg: (patientOrg) => set({ patientOrg }),
      setActiveKitId: (activeKitId) => {
        set({ activeKitId });
        syncActiveKitId(activeKitId);
      },
      reset: () => {
        set(initialState);
        syncActiveKitId(null);
        syncActivePreRnrKitId(null);
      },
    }),
    {
      name: 'context-store',
      storage: createJSONStorage(() => sessionStorage),
      // On reload (resolver doesn't re-run), recompute the active kit id from
      // the persisted context and push it into the ViomeBase holder — robust to
      // a stale persisted scalar.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // The CD kit preference set by selectConsumer(true) isn't persisted in
        // this (sessionStorage) store, but isCancerDetectClicked survives in
        // localStorage. Re-derive the preference on reload so a consumer landing
        // back in CD UI stays scoped to the CD kit, not the wellness kit.
        const preferCD =
          !state.patientOrg && getPersistedCancerDetectClicked();
        const kitId = resolveActiveKitId(state, preferCD);
        state.activeKitId = kitId;
        syncActiveKitId(kitId);
        syncActivePreRnrKitId(resolveActivePreRnrKitId(state, preferCD));
      },
    },
  ),
);

/** True when the user is in a partner/patient context. */
export const useIsPatientContext = () =>
  useContextStore((state) => state.patientOrg !== null);

/** True when the post-login experience picker should be shown. */
export const useNeedsPicker = () =>
  useContextStore((state) => state.consumerExists && state.orgs.length >= 1);

/**
 * True when the picker is required AND the user hasn't chosen yet — the picker
 * is "locked": other routes must redirect back to it until a context is picked.
 */
export const useMustSelectContext = () =>
  useContextStore(
    (state) =>
      state.consumerExists &&
      state.orgs.length >= 1 &&
      !state.hasSelectedContext,
  );

/** The currently-selected org entry (full content entry), if any. */
export const useActiveOrgEntry = () =>
  useContextStore((state) =>
    state.patientOrg
      ? (state.orgs.find((o) => o.org.id === state.patientOrg?.id) ?? null)
      : null,
  );
